import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { DesignSystemToken } from 'designSystem/types';
import FontLoader from './FontLoader';
import FontCards from './FontCards';
import RecipeSpecimens from './RecipeSpecimens';
import TypeScale from './TypeScale';
import WeightScale from './WeightScale';
import LineHeightScale from './LineHeightScale';
import * as S from './styles';

interface TypographySectionProps {
	tokens: DesignSystemToken[];
}

const DEFAULT_PREVIEW = 'The quick brown fox jumps over the lazy dog';

const TypographySection = ({ tokens }: TypographySectionProps) => {
	const [previewText, setPreviewText] = useState(DEFAULT_PREVIEW);
	const [dark, setDark] = useState(false);

	const fonts = useSelector(
		(r: RootReducer) => r.mode.designSystemContent?.config?.fonts
	);

	// Primary font family string for token scale previews
	const primaryFont = useMemo(() => {
		if (!fonts || fonts.length === 0) return 'inherit';
		const f = fonts[0];
		return `"${f.fontFamily}", ${f.fallback ?? 'sans-serif'}`;
	}, [fonts]);

	// Group typography tokens by subcategory
	const { fontSizes, fontWeights, lineHeights } = useMemo(() => {
		const sizes: DesignSystemToken[] = [];
		const weights: DesignSystemToken[] = [];
		const heights: DesignSystemToken[] = [];

		for (const t of tokens) {
			switch (t.subcategory) {
				case 'fontSizes':
					sizes.push(t);
					break;
				case 'fontWeights':
					weights.push(t);
					break;
				case 'lineHeights':
					heights.push(t);
					break;
			}
		}

		return { fontSizes: sizes, fontWeights: weights, lineHeights: heights };
	}, [tokens]);

	const displayText = previewText || DEFAULT_PREVIEW;

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	return (
		<S.Base>
			<FontLoader />

			<S.PreviewBar>
				<S.PreviewInput
					type="text"
					value={previewText}
					onChange={(e) => setPreviewText(e.target.value)}
					placeholder={DEFAULT_PREVIEW}
				/>
				<S.BgToggleLight
					type="button"
					$active={!dark}
					onClick={() => setDark(false)}
					title="Light background"
				/>
				<S.BgToggleDark
					type="button"
					$active={dark}
					onClick={() => setDark(true)}
					title="Dark background"
				/>
			</S.PreviewBar>

			<S.Subsection>
				<S.SubsectionTitle>Fonts</S.SubsectionTitle>
				<FontCards previewText={displayText} dark={dark} />
			</S.Subsection>

			<RecipeSpecimens previewText={displayText} dark={dark} />

			{fontSizes.length > 0 && (
				<S.Subsection>
					<S.SubsectionTitle>Font Sizes</S.SubsectionTitle>
					<TypeScale
						tokens={fontSizes}
						previewText={displayText}
						dark={dark}
						primaryFont={primaryFont}
					/>
				</S.Subsection>
			)}

			{fontWeights.length > 0 && (
				<S.Subsection>
					<S.SubsectionTitle>Font Weights</S.SubsectionTitle>
					<WeightScale
						tokens={fontWeights}
						dark={dark}
						primaryFont={primaryFont}
					/>
				</S.Subsection>
			)}

			{lineHeights.length > 0 && (
				<S.Subsection>
					<S.SubsectionTitle>Line Heights</S.SubsectionTitle>
					<LineHeightScale
						tokens={lineHeights}
						dark={dark}
						primaryFont={primaryFont}
					/>
				</S.Subsection>
			)}
		</S.Base>
	);
};

export default TypographySection;
